import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

import WithRouter from '../utilities/WithRouter';
import { setCurrentCampus } from '../actions';

import Accordion from '../components/Accordion';
import { knowledgeBaseArray } from '../utilities/KnowledgebaseContent';
import orientationVideo from '../assets/orientation_video.webm';
import adaptiveEnergyVideo from '../assets/adaptive_energy_management.webm';
import hvacClimateControls from '../assets/hvac_climate_controls.webm';
import icon_email from '../assets/icon_email.svg';
import icon_phone from '../assets/icon_phone.svg';
import apple_share from '../assets/icon_apple_share.svg';
import ResourcesIcon from '../components/icons/ResourcesIcon';

const Resources = ({ showMobile }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const handleToggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <ResourcesDiv className={showMobile ? 'resources-section mobile' : 'resources-section desktop'}>
      <div className={showMobile ? 'header-container mobile' : 'header-container'}>
        <div className="header">
          <ResourcesIcon
            backgroundFill={'none'}
            outerStrokeColor={'#02222B'}
            innerStrokeColor={'#02222B'}
            strokeWidth={2.5}
            width="18px"
          />
          <h1>Resources</h1>
        </div>
      </div>

      <div className="top-container">
        <div className="container support-container">
          <h2>Support</h2>

          <div className="support-details">
            <p>Hours: 9am - 5pm (PST)</p>
            <p>
              Callback within:
              <br />
              1 hour (Weekdays)
              <br />4 hours (Weekends)
            </p>
          </div>

          <div className="contact">
            <a href="mailto:support@elexity.io" className="email-us">
              <img src={icon_email} alt="email icon" width="14" height="14" />
              support@elexity.io
            </a>

            <a href="tel:5102259329" className="call-us">
              <img src={icon_phone} alt="phone icon" width="14" height="14" />
              510-225-9329
            </a>
          </div>
        </div>

        <div className="container download-container">
          <div>
            <h2>Add the Elexity App to your iPhone</h2>

            <ol>
              <li>Open Safari or Chrome: Launch the browser app on your iPhone.</li>
              <li>
                Navigate to the website:{' '}
                <a href="https://ahi.elexity.net" target="_blank">
                  https://ahi.elexity.net
                </a>
              </li>
              <li>
                Tap the Share button<img src={apple_share} height={25} width={25}></img>
              </li>
              <ul>
                <li>In Safari: located at the bottom of the screen.</li>
                <li>In Chrome: located in the address bar.</li>
              </ul>
              <li>
                Select "Add to Home Screen" -&gt; Tap the "Add to Home Screen" option from the share
                menu.
              </li>
              <li>
                Add to Home Screen -&gt; Tap "Add" to create the app-like icon on your home screen.
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="container tutorial-container">
        <h2>
          Tutorial Videos{' '}
          <span className="note">
            Note: Our video tutorials are being updated to reflect our new look and feel.
          </span>
        </h2>

        <section className="resources-section">
          <div className="resource-video">
            <h4>90 Second Orientation</h4>
            <video src={orientationVideo} width="320" height="240" controls />
          </div>

          <div className="resource-video">
            <h4>Adaptive Energy Management</h4>
            <video src={adaptiveEnergyVideo} width="320" height="240" controls />
          </div>

          <div className="resource-video">
            <h4>HVAC Climate Controls</h4>
            <video src={hvacClimateControls} width="320" height="240" controls />
          </div>
        </section>
      </div>

      <div className="container knowledgebase-container">
        <h2>Knowledge Base</h2>

        <section id="charts-data" className="kb-section">
          <div className="charts-data">
            {knowledgeBaseArray.map((item, index) => (
              <Accordion
                key={index}
                title={item.title}
                content={item.content}
                isActive={index === activeIndex}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </div>
        </section>
      </div>
    </ResourcesDiv>
  );
};

const ResourcesDiv = styled.section`
  color: #02222b;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;

  .mobile {
    .resources-section {
      justify-content: center;
    }
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }

  .top-container {
    display: flex;
    flex-flow: row wrap;
    gap: 24px;
  }

  .container {
    background: white;
    border-radius: 12px;
    box-shadow: 0px 2px 6px #02222b14;
    display: flex;
    flex-direction: column;
    font-size: 16px;
    gap: 24px;
    padding: 24px;

    h2,
    a,
    p {
      color: #02222b;
    }

    a {
      align-items: flex-end;
      display: flex;
      justify-content: flex-start;
      margin: 6px 12px;
      text-decoration: underline;

      img {
        padding-right: 6px;
      }
    }

    .support-details {
      font-weight: 500;
      margin: 0 12px 5px;

      p {
        margin: 0 0 5px;
      }
    }

    .contact {
      align-items: flex-start;
      display: flex;
      flex-flow: column;
    }

    &:last-of-type {
      margin-bottom: 24px;
    }
  }

  .support-container {
    width: 210px;
  }

  .download-container {
    width: calc(100% - 330px);

    ol {
      margin-bottom: 0;

      li {
        line-height: 1.5;

        img {
          vertical-align: bottom;
        }
      }
    }

    a {
      display: inline;
      margin: unset;
    }
  }

  .tutorial-container {
    h2 {
      align-items: center;
      display: flex;
      flex-flow: row wrap;
      gap: 24px;
    }

    .note {
      border-radius: 50px;
      font-size: 14px;
      font-style: italic;
      font-weight: 400;
      width: fit-content;
    }

    .resources-section {
      display: flex;
      flex-flow: row wrap;
      gap: 24px;

      h4 {
        font-size: 16px;
        font-weight: 600;
        margin-top: 0;
      }
    }

    .resource-video {
      width: 48%;

      video {
        height: auto;
        width: 100%;
      }
    }
  }

  .knowledgebase-container {
    .kb-section {
      .kb-title {
        align-items: center;
        display: flex;
        gap: 6px;

        svg {
          margin-top: 4px;
          transform: rotate(0deg);
        }
      }
    }
  }

  @media screen and (max-width: 767px) {
    .top-container {
      flex-flow: column;

      .support-container,
      .download-container {
        width: calc(100% - 48px);
      }

      .support-container {
        flex-flow: row wrap;
        justify-content: space-between;

        h2 {
          width: 100%;
        }

        p {
          line-height: 1.5;
        }
      }

      .download-container {
        ol {
          li {
            line-height: 1.5;
          }
        }
      }
    }

    .tutorial-container .resource-video {
      width: 100%;
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    currentCampusId: state.currentCampusId,
    currentBuildingId: state.currentBuildingId,
    currentZoneId: state.currentZoneId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    currentUserData: state.currentUserData,
    campuses: state.campuses,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentCampus: (campusId) => {
      dispatch(setCurrentCampus(campusId));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(Resources));
